/** Shows the sign-in form until the API accepts the session cookie. */

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent, type ReactNode } from 'react'

import { api, ApiError, SIGNED_OUT_EVENT } from '@/api/client'
import { Button } from '@/components/ui/Button'
import { Label } from '@/components/ui/Primitives'

export function AuthGate({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const session = useQuery({ queryKey: ['session'], queryFn: api.session, staleTime: Infinity })

  useEffect(() => {
    const onSignedOut = () => queryClient.invalidateQueries({ queryKey: ['session'] })
    window.addEventListener(SIGNED_OUT_EVENT, onSignedOut)
    return () => window.removeEventListener(SIGNED_OUT_EVENT, onSignedOut)
  }, [queryClient])

  if (session.isPending) return <div className="grid min-h-dvh place-items-center text-[14px] text-ink-muted">Loading…</div>
  if (session.data && !session.data.authenticated) return <LoginPage />
  return <>{children}</>
}

function LoginPage() {
  const queryClient = useQueryClient()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const session = await api.login(username.trim(), password)
      queryClient.setQueryData(['session'], session)
      await queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'session' })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not sign in')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="grid min-h-dvh place-items-center bg-surface-muted px-4">
      <form onSubmit={submit} className="w-full max-w-[360px] rounded-card border border-line bg-surface p-7 shadow-card">
        <div className="mb-6 flex items-center gap-2.5">
          <div className="grid h-8 w-8 place-items-center rounded-[9px] bg-ink text-[13px] font-semibold text-white">JS</div>
          <div className="leading-tight">
            <p className="text-[14.5px] font-semibold">Job Scheduler</p>
            <p className="text-[11.5px] text-ink-muted">Sign in to continue</p>
          </div>
        </div>
        <div className="space-y-4">
          <label className="block">
            <Label>Username</Label>
            <input className="field-box" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" autoFocus required />
          </label>
          <label className="block">
            <Label>Password</Label>
            <input className="field-box" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
          </label>
          {error && <p className="text-[13px] text-danger">{error}</p>}
          <Button type="submit" fullWidth loading={busy}>
            Sign in
          </Button>
        </div>
      </form>
    </div>
  )
}
